import React, { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../api/axios';
import { mediaSrc } from '../api/media';
import { KidsTrackWordmark } from './Cartoons';
import NotificationBell, { BellItem } from './NotificationBell';
import FeedbackModal from './FeedbackModal';

interface ChatNotif { conversationId: number; title: string; unread: number; }
interface ParentNotif { friendRequests: number; chats: ChatNotif[]; totalUnread: number; }

interface Tab {
  label: string;
  to: string;
  emoji: string;
  match: string[];
  color: string;
}

const TABS: Tab[] = [
  { label: 'Home', to: '/parent/home', emoji: '🏠', match: ['/parent/home', '/parent/dashboard'], color: '#FACC15' },
  { label: 'KidsTrack', to: '/parent/learn', emoji: '🚀', match: ['/parent/learn', '/parent/kids'], color: '#FB923C' },
  { label: 'Image', to: '/parent/photos', emoji: '📷', match: ['/parent/photos'], color: '#34D399' },
  { label: 'Video', to: '/parent/videos', emoji: '🎬', match: ['/parent/videos'], color: '#38BDF8' },
  { label: 'Friends & Groups', to: '/parent/social', emoji: '🤝', match: ['/parent/social', '/parent/friends', '/parent/groups', '/parent/chat'], color: '#A78BFA' },
];

const ParentLayout: React.FC = () => {
  const { name, logout } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [notif, setNotif] = useState<ParentNotif>({ friendRequests: 0, chats: [], totalUnread: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get('/api/parent/profile').then((r) => setAvatar(r.data?.profilePhotoUrl || null)).catch(() => {});
  }, []);

  // Poll notifications (unread chats + incoming friend requests) every 15s.
  useEffect(() => {
    const load = () => api.get('/api/parent/notifications').then((r) => setNotif(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const badgeCount = notif.friendRequests + notif.totalUnread;
  const bellItems: BellItem[] = [
    ...notif.chats.map((c) => ({
      key: 'chat-' + c.conversationId,
      emoji: '💬',
      label: c.title,
      sublabel: `${c.unread} new message${c.unread === 1 ? '' : 's'}`,
      onClick: () => navigate(`/parent/social?tab=chat&cid=${c.conversationId}`),
    })),
    ...(notif.friendRequests > 0
      ? [{
          key: 'friend-requests',
          emoji: '🤝',
          label: `${notif.friendRequests} friend request${notif.friendRequests === 1 ? '' : 's'}`,
          sublabel: 'Tap to review',
          onClick: () => navigate('/parent/social'),
        }]
      : []),
  ];

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/parent/login');
  };

  const handleExportData = async () => {
    setMenuOpen(false);
    try {
      const res = await api.get('/api/parent/account/export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kidtrack-data.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Could not export your data. Please try again.');
    }
  };

  const handleDeleteAccount = async () => {
    setMenuOpen(false);
    if (!window.confirm('Delete your account? This permanently removes you, your children, and all their data. This cannot be undone.')) return;
    if (!window.confirm('Are you absolutely sure? This is permanent.')) return;
    try {
      await api.delete('/api/parent/account');
      logout();
      navigate('/parent/login');
    } catch {
      alert('Could not delete your account. Please try again.');
    }
  };

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post('/api/parent/profile-photo', fd);
      setAvatar(r.data?.url || null);
    } catch { alert('Failed to upload photo'); }
    finally { e.target.value = ''; }
  };

  const isActive = (t: Tab) => t.match.some((m) => pathname === m || pathname.startsWith(m + '/'));

  return (
    <div className="min-h-screen bg-kid-bg flex flex-col">
      {/* Header */}
      <header className="bg-kid-header text-white rounded-b-[2rem] shadow-lg px-4 pt-4 pb-6 relative z-30">
        <div className="max-w-2xl mx-auto w-full">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/85 text-sm font-semibold">Hi, {name}! 👋</p>
              <KidsTrackWordmark className="text-3xl drop-shadow-sm" />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-10 h-10 rounded-full bg-white/20 ring-2 ring-white/40 overflow-hidden flex items-center justify-center text-lg cursor-pointer" title="Change photo">
                {avatar ? (
                  <img src={mediaSrc(avatar)} alt="me" className="w-full h-full object-cover" />
                ) : (
                  <span>👤</span>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              </label>
              <NotificationBell total={badgeCount} items={bellItems} />
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((o) => !o)}
                  className="w-10 h-10 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-xl font-bold leading-none"
                  aria-label="Menu"
                >
                  ⋮
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl py-2 z-50 text-gray-700">
                    <button
                      onClick={() => { setMenuOpen(false); navigate('/parent/help'); }}
                      className="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      📘 Help &amp; Instructions
                    </button>
                    <button
                      onClick={() => { setMenuOpen(false); setFeedbackOpen(true); }}
                      className="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      💬 Feedback
                    </button>
                    <button
                      onClick={handleExportData}
                      className="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      ⬇️ Download my data
                    </button>
                    <div className="my-1 border-t border-gray-100" />
                    <button
                      onClick={handleDeleteAccount}
                      className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50 flex items-center gap-2"
                    >
                      🗑️ Delete account
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50 flex items-center gap-2"
                    >
                      🚪 Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1 w-full max-w-2xl mx-auto px-4 pt-5 pb-28">
        <Outlet />
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          {TABS.map((t) => {
            const active = isActive(t);
            return (
              <Link key={t.to} to={t.to} className="flex flex-col items-center gap-1 py-2.5 text-center relative">
                <span
                  className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl transition"
                  style={active ? { background: t.color, boxShadow: `0 6px 14px ${t.color}66` } : { background: '#F3F4F6' }}
                >
                  {t.emoji}
                </span>
                {t.label === 'Friends & Groups' && badgeCount > 0 && (
                  <span className="absolute top-1 right-3 bg-red-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-none">
                    {badgeCount}
                  </span>
                )}
                <span className={`text-[10px] font-bold leading-tight ${active ? 'text-gray-800' : 'text-gray-400'}`} style={{ maxWidth: 64 }}>
                  {t.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
};

export default ParentLayout;
